import React, { useEffect, useState, Suspense, lazy } from "react";
import "./App.css";
import { Loader2 } from "lucide-react";
import { api } from "./lib/api";
import { AuthProvider, useAuth } from "./lib/auth";
import SailorSidebar from "./components/SailorSidebar";
import SailorDetail from "./components/SailorDetail";
import OverviewPanel from "./components/OverviewPanel";
import LoginScreen from "./components/LoginScreen";
import UrgentIssuesChecklist from "./components/UrgentIssuesChecklist";
import DailyBriefingModal from "./components/DailyBriefingModal";
import PublicSharePage from "./components/PublicSharePage";
import ConnectivityBanner from "./components/ConnectivityBanner";
import HelpAssistant from "./components/HelpAssistant";
import AppHeader from "./components/dashboard/AppHeader";
import HeroBanner from "./components/dashboard/HeroBanner";
import DemoTourOverlay from "./components/DemoTourOverlay";
import SuggestionModal from "./components/SuggestionModal";
import MobileRosterSheet from "./components/MobileRosterSheet";
import useDailyModals from "./hooks/useDailyModals";
// Auto-reload was disabled 2026-02-17 (user reported "auto refreshes every
// minute" as very important). Import removed; hook file left in place.
// import useAutoReload from "./hooks/useAutoReload";
import makeHelpAssistantNavigate from "./lib/helpAssistantNavigate";
import { TABS } from "./constants/tabs";

// Non-default tabs — lazy-load so their bundles (Recharts, wizard modals,
// heatmaps) don't slow the first paint. Suspense fallback below shows a
// spinner while the chunk is fetched.
const CompareView = lazy(() => import("./components/CompareView"));
const DataEditor = lazy(() => import("./components/DataEditor"));
const BiometricsView = lazy(() => import("./components/BiometricsView"));
const ScheduleView = lazy(() => import("./components/ScheduleView"));
const WearablesView = lazy(() => import("./components/WearablesView"));
const DietPanel = lazy(() => import("./components/DietPanel"));
const SupplementsView = lazy(() => import("./components/SupplementsView"));
const ReportsPanel = lazy(() => import("./components/ReportsPanel"));
const DataSpreadsheetView = lazy(() => import("./components/DataSpreadsheetView"));
const BaselineSpreadsheetView = lazy(() => import("./components/BaselineSpreadsheetView"));
const MarkerTrendsView = lazy(() => import("./components/MarkerTrendsView"));
const ResponseTracker = lazy(() => import("./components/ResponseTracker"));
const AdvisoryView = lazy(() => import("./components/AdvisoryView"));

function App() {
  // Detect the public share route before the auth gate runs — recipients of
  // a wa.me/email share link must be able to open the report without logging
  // in. Path format: /share/<32-char-hex-token>
  const shareMatch = typeof window !== "undefined" && window.location.pathname.match(/^\/share\/([a-zA-Z0-9]+)/);
  if (shareMatch) {
    return <PublicSharePage token={shareMatch[1]} />;
  }
  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  );
}

function AuthGate() {
  const { status, isAuthenticated } = useAuth();
  if (status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="auth-loading">
        <div className="flex items-center gap-2 text-slate-400">
          <Loader2 className="animate-spin" size={18} />
          <span className="font-mono-data text-sm">Loading…</span>
        </div>
      </div>
    );
  }
  if (!isAuthenticated) return <LoginScreen />;
  return <Dashboard />;
}

function Dashboard() {
  const [overview, setOverview]   = useState(null);
  const [parameters, setParameters] = useState([]);
  const [sailors, setSailors]     = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab]             = useState("editor");
  const [demoOpen, setDemoOpen]   = useState(false);
  const [suggestionOpen, setSuggestionOpen] = useState(false);
  const [injuryConstants, setInjuryConstants] = useState(null);
  const [injurySummary, setInjurySummary]     = useState({ active: 0, recovering: 0, on_rest_count: 0, by_sailor: {} });

  // The roster payload includes archived athletes so the sidebar can
  // render its "Archived" tab. Downstream views (CompareView, editor
  // dropdowns) want ONLY active athletes — keep both slices handy.
  const activeSailors = React.useMemo(
    () => (sailors || []).filter((s) => !s.archived),
    [sailors],
  );

  // Bridge event for the DemoTourOverlay — it dispatches `demo:goto`
  // with `{tab: "..."}` so we can switch top-level tabs from a
  // component that doesn't need `setTab` threaded through its props.
  // The `pickAthlete` flag also seeds the first athlete when the
  // destination view is per-athlete (Data Editor / Trend / Marker
  // Trends) so those scenes render meaningful content instead of an
  // empty sidebar.
  useEffect(() => {
    const onGoto = (e) => {
      const detail = e?.detail || {};
      if (detail.tab) setTab(detail.tab);
      if (detail.pickAthlete && sailors.length > 0 && !selectedId) {
        setSelectedId(sailors[0].id);
      }
    };
    const onSwitch = (e) => {
      const next = e?.detail;
      if (typeof next === "string") setTab(next);
    };
    window.addEventListener("demo:goto", onGoto);
    window.addEventListener("ych:switch-tab", onSwitch);
    return () => {
      window.removeEventListener("demo:goto", onGoto);
      window.removeEventListener("ych:switch-tab", onSwitch);
    };
  }, [sailors, selectedId]);

  // Global "Esc = back to Data Editor" — the Edit sub-tab was removed
  // 2026-02-14 because it was a self-reference (clicking Edit from the
  // Data Editor did nothing visible). Coaches asked instead for Esc to
  // return them to the Data Editor default view from any of the athlete
  // sub-views (Trend / Marker Trends) or from any modal launched off
  // those views. We *don't* gate on modal presence — the modal's own
  // `useEscClose` fires first (or in the same tick) and closes it,
  // React then unmounts the parent sub-view when we switch to
  // `editor`, so a single Esc press dismisses both cleanly.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape" && e.key !== "Esc") return;
      // Ignore if the user is typing in an input/textarea/select — we
      // don't want to steal Esc from form UX.
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (tab === "sailor" || tab === "marker_trends") {
        setTab("editor");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tab]);

  const { user } = useAuth();
  const {
    urgentOpen, briefingOpen,
    openUrgent, closeUrgent,
    openBriefing, closeBriefing,
  } = useDailyModals(user?.email);

  // Gmail-style silent auto-reload — polls `/api/version`, silently
  // Auto-reload disabled 2026-02-17 at user's explicit request:
  // "The system is auto refreshing every minute. Needs to stop VERY
  // IMPORTANT". Coach hits browser refresh manually now. The hook
  // and its `/api/version-public` polling are left in the codebase
  // in case we want to reintroduce it behind a feature flag later.
  // useAutoReload();

  const loadInjurySummary = async () => {
    try {
      const r = await api.get("/injuries/summary");
      setInjurySummary(r.data);
    } catch (err) {
      // Injuries are non-critical chrome — silently keep previous counts
      // in the UI, but log so a persistent backend outage is visible in
      // DevTools instead of silently degrading the dashboard.
      console.warn("[injuries] summary load failed:", err?.message);
    }
  };

  const loadAll = async (preferredId) => {
    const [ov, sl] = await Promise.all([
      api.get("/overview").then((r) => r.data),
      // include_archived=true so the sidebar's "Archived" tab is populated
      // without a second call. Every downstream count / filter / KPI still
      // scopes to active-only via `s.archived === true` checks server-side.
      api.get("/sailors?include_archived=true").then((r) => r.data),
    ]);
    setOverview(ov);
    setSailors(sl);
    if (preferredId === "__cleared__") {
      setSelectedId(null);
      return;
    }
    if (preferredId) { setSelectedId(preferredId); return; }
    // keep current selection if it still exists, else pick the ACTIVE
    // athlete with the most records (falling back to any athlete only
    // if the roster is entirely archived — which shouldn't happen but
    // means the auth-gate is deterministic even in that edge case).
    setSelectedId((prev) => {
      if (prev && sl.some((s) => s.id === prev)) return prev;
      if (sl.length === 0) return null;
      const activeOnly = sl.filter((s) => !s.archived);
      const source = activeOnly.length ? activeOnly : sl;
      return [...source].sort((a, b) => b.record_count - a.record_count)[0].id;
    });
  };

  useEffect(() => {
    api.get("/parameters").then((r) => setParameters(r.data));
    api.get("/injuries/constants").then((r) => setInjuryConstants(r.data)).catch(() => {});
    loadAll();
    loadInjurySummary();
  }, []);

  // Reload when a deleted entity is restored from the Archive.
  useEffect(() => {
    const handler = () => loadAll();
    window.addEventListener("ych:data-changed", handler);
    return () => window.removeEventListener("ych:data-changed", handler);
  }, []);

  const onHelpNavigate = makeHelpAssistantNavigate({ setTab, setSelectedId });

  return (
    <div className="App min-h-screen">
      <AppHeader
        tab={tab}
        onTabChange={setTab}
        onOpenUrgent={openUrgent}
        onOpenBriefing={openBriefing}
        onOpenDemo={() => setDemoOpen(true)}
        onOpenSuggestion={() => setSuggestionOpen(true)}
      />

      {/* Hero banner — full sea backdrop + tagline + KPI tiles only on
          Squad Overview. On other tabs it collapses to a slim page-title
          strip so the tab's own content (Biometrics matrix / Data Editor
          / Schedule) has substantially more vertical space to breathe. */}
      <HeroBanner tab={tab} overview={overview} injurySummary={injurySummary} />

      {/* Main body */}
      <main className="px-4 md:px-8 pt-3 pb-6 md:py-4 max-w-[1600px] mx-auto">
        {/* Mobile / tablet roster shortcut — the desktop sidebar is
            hidden on < lg because a 300 px column eats the top of
            every athlete page on tablets. This floating "N athletes"
            bubble opens the same roster as a bottom-sheet so the
            coach can jump between athletes without losing the
            currently-open card. */}
        {(tab === "sailor" || tab === "editor" || tab === "marker_trends") && (
          <MobileRosterSheet
            sailors={sailors}
            selectedId={selectedId}
            onSelect={(id) => { setSelectedId(id); if (tab === "overview") setTab("sailor"); }}
            injuriesBySailor={injurySummary.by_sailor}
          />
        )}
        {/* Squad Overview, Compare, Biometrics and Schedule are broad views
            with their own athlete pickers — the outer roster sidebar would
            just duplicate content. Collapse the grid to full-width on those
            tabs so the panel gets the whole 1600px. */}
        <div className={`grid grid-cols-1 ${
          tab === "sailor" || tab === "editor" || tab === "marker_trends"
            ? "lg:grid-cols-[300px,1fr]"
            : ""
        } gap-5 md:gap-6`}>
          {(tab === "sailor" || tab === "editor" || tab === "marker_trends") && (
            <aside className="hidden lg:block">
              <SailorSidebar
                sailors={sailors}
                selectedId={selectedId}
                onSelect={(id) => { setSelectedId(id); if (tab === "overview") setTab("sailor"); }}
                injuriesBySailor={injurySummary.by_sailor}
              />
            </aside>
          )}

          <section className="min-w-0">
            {/* Athlete-level sub-tabs (Trend / Marker Trends / Edit) now
                live INSIDE each of the three athlete views — attached to
                the athlete card — instead of floating above the pane. See
                <AthleteSubTabs> rendered by SailorDetail, MarkerTrendsView
                and EditorToolbar. Each view receives `activeTab` +
                `onSelectTab` so the switcher stays in sync. */}
            {tab === "overview" && (
              <OverviewPanel
                overview={overview}
                parameters={parameters}
                injurySummary={injurySummary}
                onSelectSailor={(id) => { setSelectedId(id); setTab("sailor"); }}
              />
            )}
            {tab === "sailor" && (
              <SailorDetail
                sailorId={selectedId}
                parameters={parameters}
                injuryConstants={injuryConstants}
                onInjuriesChanged={loadInjurySummary}
                activeTab={tab}
                onSelectTab={setTab}
              />
            )}
            {/* Non-default tabs share one Suspense boundary — the fallback
                is a lightweight spinner so switching tabs never shows a
                blank pane while the chunk is fetched. */}
            <Suspense
              fallback={
                <div className="card p-8 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
                  <Loader2 size={16} className="animate-spin text-cyan-300" /> Loading…
                </div>
              }
            >
              {tab === "compare" && (
                <CompareView sailors={activeSailors} parameters={parameters} />
              )}
              {tab === "response" && (
                <ResponseTracker
                  onOpenAthlete={(id) => { setSelectedId(id); setTab("sailor"); }}
                />
              )}
              {tab === "advisory" && (
                <AdvisoryView
                  onOpenAthlete={(id) => { setSelectedId(id); setTab("sailor"); }}
                />
              )}
              {tab === "editor" && (
                <DataEditor
                  sailorId={selectedId}
                  parameters={parameters}
                  sailors={activeSailors}
                  onSelectSailor={(id) => setSelectedId(id)}
                  onDataChanged={(idOrFlag) => loadAll(idOrFlag)}
                  injuryConstants={injuryConstants}
                  onInjuriesChanged={loadInjurySummary}
                  activeTab={tab}
                  onSelectTab={setTab}
                />
              )}
              {tab === "marker_trends" && (
                <MarkerTrendsView
                  sailorId={selectedId}
                  parameters={parameters}
                  activeTab={tab}
                  onSelectTab={setTab}
                />
              )}
              {tab === "biometrics" && (
                <BiometricsView />
              )}
              {tab === "spreadsheet" && (
                <DataSpreadsheetView
                  injuryConstants={injuryConstants}
                  parameters={parameters}
                  onDataChanged={() => loadAll()}
                />
              )}
              {tab === "baselines" && (
                <BaselineSpreadsheetView
                  onDataChanged={() => loadAll()}
                />
              )}
              {tab === "schedule" && (
                <ScheduleView
                  onSelectSailor={(id) => { setSelectedId(id); setTab("editor"); }}
                />
              )}
              {tab === "wearables" && (
                <WearablesView />
              )}
              {tab === "diet" && (
                <DietPanel />
              )}
              {tab === "supplements" && (
                <SupplementsView />
              )}
              {tab === "reports" && (
                <ReportsPanel />
              )}
            </Suspense>
          </section>
        </div>

        <footer className="mt-12 pt-6 border-t border-[#1E304B] text-[11px] text-slate-500 flex flex-wrap items-center justify-between gap-2">
          <div>
            Yacht Club of Hyderabad · Medical analytics ·{" "}
            <span className="font-mono-data text-slate-400">
              {overview?.total_sailors || 0} athletes · {overview?.total_records || 0} records
            </span>
          </div>
          <div className="font-mono-data">Reference ranges per Tata 1mg lab; status flags are advisory.</div>
        </footer>
      </main>

      <UrgentIssuesChecklist
        open={urgentOpen}
        onClose={closeUrgent}
        onPrintClose={closeUrgent}
      />

      <DailyBriefingModal
        open={briefingOpen}
        onClose={closeBriefing}
        onOpenAthlete={(sailorId) => {
          setSelectedId(sailorId);
          setTab("editor");
          closeBriefing();
        }}
      />

      {/* Global save-failure / offline banner — listens for CustomEvents
          dispatched by `useUnsavedGuard` and browser online/offline events. */}
      <ConnectivityBanner />

      {/* Self-running product tour — triggered by "▶ Demo tour" in the
          profile menu. Drives the live app: switches tabs, opens the
          key modals, and narrates each scene via browser TTS. */}
      <DemoTourOverlay open={demoOpen} onClose={() => setDemoOpen(false)} />

      {/* Suggest an improvement — a single modal with an entry
          section on top and a shared community feed below. Every
          logged-in user can submit and see the full list; admins get
          extra Mark-done / Delete controls on each feed row. */}
      <SuggestionModal
        open={suggestionOpen}
        onClose={() => setSuggestionOpen(false)}
      />

      {/* In-app "Ask the app" help assistant. The bottom-right bubble
          opens a Claude-powered chat panel that can jump the coach to
          any tab / User-menu modal via whitelisted deep-link routes. */}
      <HelpAssistant
        currentScreen={TABS.find((t) => t.id === tab)?.label || tab}
        currentSailorId={selectedId}
        currentSailorName={overview?.sailors?.find((s) => s.id === selectedId)?.name}
        onNavigate={onHelpNavigate}
      />
    </div>
  );
}

export default App;
